import { useState, useEffect, useCallback } from 'react'
import { io, Socket } from 'socket.io-client'
import api from '../lib/api'

interface Visitor {
  visitorId: string
  sessionId?: string
  userAgent?: string
  url?: string
  timestamp: string
  hasAgent: boolean
  agentId?: string
  handlingState?: 'ai_handling' | 'agent_handling' | 'agent_stalled'
  assignedAgentId?: string
  lastActivityAt?: string
  contactInfo?: {
    firstName?: string
    email?: string
    phoneNumber?: string
    companyName?: string
  }
  messages: Array<{
    id: string
    content: string
    senderType: 'visitor' | 'agent' | 'bot' | 'system'
    timestamp: string
  }>
}

interface WebSocketState {
  isConnected: boolean
  visitors: Visitor[]
  queueStats: any
  lastHeartbeat: number
  connectionHealth: 'healthy' | 'degraded' | 'disconnected'
}

// Singleton state - shared across all components
let globalState: WebSocketState = {
  isConnected: false,
  visitors: [],
  queueStats: {},
  lastHeartbeat: Date.now(),
  connectionHealth: 'disconnected'
}

// Singleton socket reference
let socketInstance: Socket | null = null
let agentId: string = ''

// Optimistic-takeover bookkeeping. When an agent clicks "take chat" we
// flip the visitor row to agent-handling immediately so the UI feels
// instant, but we stash the prior values here so we can revert cleanly
// if the server says no (chat already claimed by another agent, org
// mismatch, etc.). Keyed by visitorId.
type OptimisticSnapshot = Pick<Visitor, 'hasAgent' | 'agentId' | 'handlingState' | 'assignedAgentId'>
const pendingTakeovers: Map<string, OptimisticSnapshot> = new Map()

// Subscribers for state changes
const subscribers = new Set<() => void>()

function notifySubscribers() {
  subscribers.forEach(callback => callback())
}

function setGlobalState(updater: (prev: WebSocketState) => WebSocketState) {
  globalState = updater(globalState)
  notifySubscribers()
}

// Singleton connect function
async function connectSocket(newAgentId: string) {
  if (socketInstance?.connected) {
    console.log('WebSocket already connected, skipping...')
    return
  }

  agentId = newAgentId
  // Use VITE_WS_URL, fall back to VITE_API_URL, then localhost for dev
  const wsUrl = import.meta.env.VITE_WS_URL || import.meta.env.VITE_API_URL || 'http://localhost:3000'

  console.log('Connecting to WebSocket:', wsUrl, 'as agent:', agentId)

  // Fetch active chats from API before connecting to WebSocket
  try {
    const response = await api.get('/api/agent/active-chats')
    const data = response.data

    console.log('Loaded active chats from API:', data.sessions?.length || 0)

    if (data.sessions && data.sessions.length > 0) {
      const visitors = data.sessions.map((session: any) => ({
        visitorId: session.visitorId,
        sessionId: session.id,
        userAgent: session.metadata?.userAgent,
        url: session.metadata?.url,
        timestamp: session.startedAt,
        hasAgent: !!session.agentId,
        agentId: session.agentId,
        handlingState: session.handlingState || (session.agentId ? 'agent_handling' : 'ai_handling'),
        assignedAgentId: session.assignedAgentId,
        lastActivityAt: session.lastActivityAt,
        contactInfo: session.metadata?.contactInfo || null,
        messages: session.messages || []
      }))

      setGlobalState(prev => ({ ...prev, visitors }))
    }
  } catch (error) {
    console.error('Failed to load active chats:', error)
  }

  const authToken = localStorage.getItem('app_auth_token') || ''

  socketInstance = io(wsUrl, {
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 1000,
    // Capped exponential backoff. Previously this was 5 attempts at 1s
    // intervals — five seconds of trying and then socket.io gave up
    // forever, which is why every API restart (deploys, even brief blips)
    // left the dashboard with a permanent red "Connection lost" banner
    // until the user did a full page refresh.
    reconnectionAttempts: Infinity,
    reconnectionDelayMax: 30000,
    randomizationFactor: 0.5,
    query: {
      agentId: newAgentId,
      type: 'agent'
    },
    auth: {
      token: authToken
    }
  })

  const socket = socketInstance

  socket.on('connect', () => {
    console.log('Agent WebSocket connected')
    setGlobalState(prev => ({
      ...prev,
      isConnected: true,
      lastHeartbeat: Date.now(),
      connectionHealth: 'healthy'
    }))
  })

  socket.on('disconnect', (reason) => {
    console.log('Agent WebSocket disconnected:', reason)
    // Reset lastHeartbeat so the periodic health timer (which compares
    // Date.now() against lastHeartbeat) can't briefly flip us back to
    // 'healthy' during a real outage just because the last pong was
    // recent. While disconnected, socket.connected is the source of truth.
    setGlobalState(prev => ({
      ...prev,
      isConnected: false,
      lastHeartbeat: 0,
      connectionHealth: 'disconnected'
    }))
  })

  // Fires when socket.io's built-in reconnection succeeds. Without this
  // the banner could stay red after a successful reconnect until the
  // first pong landed (~10s later).
  socket.io.on('reconnect', (attempt) => {
    console.log(`Agent WebSocket reconnected after ${attempt} attempt(s)`)
    setGlobalState(prev => ({
      ...prev,
      isConnected: true,
      lastHeartbeat: Date.now(),
      connectionHealth: 'healthy'
    }))
    // Catch up on any visitor:join / visitor:message events that fired
    // while we were disconnected — without this, a visitor who started
    // chatting during the outage stays invisible until the next 60s
    // revalidation tick.
    api.get('/api/agent/active-chats').then((response) => {
      if (!response.data?.success || !Array.isArray(response.data.sessions)) return
      const sessions = response.data.sessions
      setGlobalState(prev => {
        const haveIds = new Set(prev.visitors.map(v => v.visitorId))
        const fresh = sessions
          .filter((s: any) => !haveIds.has(s.visitorId))
          .map((session: any) => ({
            visitorId: session.visitorId,
            sessionId: session.id,
            userAgent: session.metadata?.userAgent,
            url: session.metadata?.url,
            timestamp: session.startedAt,
            hasAgent: !!session.agentId,
            agentId: session.agentId,
            handlingState: session.handlingState || (session.agentId ? 'agent_handling' : 'ai_handling'),
            assignedAgentId: session.assignedAgentId,
            lastActivityAt: session.lastActivityAt,
            contactInfo: session.metadata?.contactInfo || null,
            messages: session.messages || []
          }))
        if (fresh.length === 0) return prev
        return { ...prev, visitors: [...prev.visitors, ...fresh] }
      })
    }).catch(() => { /* non-critical — periodic revalidation will retry */ })
  })

  socket.io.on('reconnect_attempt', (attempt) => {
    // Quietly log so we can see how long an outage lasted; no UI change —
    // the disconnect handler already set us to 'disconnected'.
    console.log(`Agent WebSocket reconnect attempt #${attempt}`)
  })

  socket.on('pong', () => {
    setGlobalState(prev => ({
      ...prev,
      lastHeartbeat: Date.now(),
      connectionHealth: 'healthy'
    }))
  })

  socket.on('agent:connected', (data) => {
    console.log('Agent connected:', data)
  })

  socket.on('visitor:connected', (data) => {
    console.log('Visitor connected:', data)
    setGlobalState(prev => {
      const existingIndex = prev.visitors.findIndex(v => v.visitorId === data.visitorId)

      if (existingIndex >= 0) {
        const updatedVisitors = [...prev.visitors]
        updatedVisitors[existingIndex] = {
          ...updatedVisitors[existingIndex],
          timestamp: data.timestamp,
          ...(data.sessionId && { sessionId: data.sessionId }),
          ...(data.contactInfo && { contactInfo: data.contactInfo })
        }
        return { ...prev, visitors: updatedVisitors }
      }

      return {
        ...prev,
        visitors: [...prev.visitors, {
          visitorId: data.visitorId,
          sessionId: data.sessionId,
          userAgent: data.userAgent,
          url: data.url,
          timestamp: data.timestamp,
          hasAgent: false,
          handlingState: 'ai_handling' as const,
          contactInfo: data.contactInfo,
          messages: []
        }]
      }
    })
  })

  socket.on('visitor:message', (data) => {
    console.log('Visitor message:', data)
    setGlobalState(prev => {
      const existing = prev.visitors.find(v => v.visitorId === data.visitorId)

      const newMessage = {
        id: data.messageId || Date.now().toString(),
        content: data.message,
        senderType: (data.senderType || 'visitor') as 'visitor' | 'agent' | 'bot' | 'system',
        timestamp: data.timestamp,
      }

      if (existing) {
        // Existing visitor — append, dedup by content + 2s timestamp window.
        const isDuplicate = existing.messages.some(m =>
          m.content === newMessage.content &&
          Math.abs(new Date(m.timestamp).getTime() - new Date(newMessage.timestamp).getTime()) < 2000
        )
        if (isDuplicate) {
          console.log('Duplicate visitor message ignored:', newMessage.content)
          return prev
        }
        return {
          ...prev,
          visitors: prev.visitors.map(v =>
            v.visitorId === data.visitorId
              ? { ...v, messages: [...v.messages, newMessage] }
              : v
          ),
        }
      }

      // No existing visitor row in state — this happens when the agent's
      // dashboard joined the org room AFTER the visitor session was
      // created (e.g. dashboard reload, late connect, or
      // visitor:connected event got missed). Previously we silently
      // dropped the message; now we stub a minimal visitor row so the
      // message survives. The periodic revalidation tick (15s) will
      // hydrate the metadata (userAgent, contactInfo, handlingState,
      // etc.) from the server.
      console.warn('[visitor:message] Unknown visitorId — creating stub so message is not dropped:', data.visitorId)
      const stub: Visitor = {
        visitorId: data.visitorId,
        sessionId: data.sessionId,
        timestamp: data.timestamp || new Date().toISOString(),
        hasAgent: false,
        handlingState: 'ai_handling',
        messages: [newMessage],
      }
      return { ...prev, visitors: [...prev.visitors, stub] }
    })
  })

  socket.on('visitor:disconnected', (data) => {
    console.log('Visitor disconnected:', data)
  })

  socket.on('visitor:updated', (data) => {
    setGlobalState(prev => ({
      ...prev,
      visitors: prev.visitors.map(v =>
        v.visitorId === data.visitorId
          ? { ...v, contactInfo: { ...v.contactInfo, ...data.contactInfo } }
          : v
      )
    }))
  })

  socket.on('takeover:success', (data) => {
    console.log('[TAKEOVER] Takeover successful:', data)
    // The optimistic flip in takeChatFromVisitor already moved the
    // visitor row to agent-handling. We re-apply the same shape here as
    // a belt-and-braces reconciliation in case any other event landed
    // in between and clobbered fields. Then drop the rollback snapshot.
    pendingTakeovers.delete(data.visitorId)
    setGlobalState(prev => ({
      ...prev,
      visitors: prev.visitors.map(v =>
        v.visitorId === data.visitorId
          ? { ...v, hasAgent: true, agentId: agentId, handlingState: 'agent_handling' as const, assignedAgentId: agentId }
          : v
      )
    }))
  })

  socket.on('takeover:error', (error) => {
    console.error('[TAKEOVER] Takeover failed:', error)
    // Revert the optimistic flip — the visitor row goes back to whatever
    // state it was in when the agent clicked. Without this revert the
    // dashboard would lie about the visitor's handling state until a
    // poll cycle reconciled it 15 seconds later.
    const visitorId = error?.visitorId
    if (visitorId && pendingTakeovers.has(visitorId)) {
      const prior = pendingTakeovers.get(visitorId)!
      pendingTakeovers.delete(visitorId)
      setGlobalState(prev => ({
        ...prev,
        visitors: prev.visitors.map(v =>
          v.visitorId === visitorId ? { ...v, ...prior } : v
        )
      }))
    } else {
      // No specific visitorId attached — fall back to clearing all
      // pending takeovers to avoid leaking stale optimistic state.
      pendingTakeovers.clear()
    }
    alert(`Failed to take over chat: ${error?.message || 'Unknown error'}`)
  })

  socket.on('chat:started', (data) => {
    console.log('Chat started:', data)
    setGlobalState(prev => ({
      ...prev,
      visitors: prev.visitors.map(v =>
        v.visitorId === data.visitorId
          ? {
              ...v,
              hasAgent: true,
              agentId: agentId,
              handlingState: 'agent_handling' as const,
              assignedAgentId: agentId,
              messages: data.chatHistory?.map((msg: any) => ({
                id: msg.id,
                content: msg.content,
                senderType: msg.type,
                timestamp: msg.timestamp
              })) || v.messages
            }
          : v
      )
    }))
  })

  socket.on('chat:stalled', (data) => {
    console.log('[CHAT] Stalled:', data.sessionId)
    setGlobalState(prev => ({
      ...prev,
      visitors: prev.visitors.map(v =>
        v.sessionId === data.sessionId || v.visitorId === data.visitorId
          ? { ...v, handlingState: 'agent_stalled' as const }
          : v
      )
    }))
  })

  // Remove completed chats from live list
  socket.on('chat:completed', (data) => {
    console.log('[CHAT] Completed:', data.sessionId, 'reason:', data.reason)
    setGlobalState(prev => ({
      ...prev,
      visitors: prev.visitors.filter(v =>
        v.visitorId !== data.visitorId && v.sessionId !== data.sessionId
      )
    }))
  })

  socket.on('error', (error) => {
    console.error('WebSocket error:', error)
  })

  // Start health monitoring
  startHealthMonitoring()
}

let pingInterval: NodeJS.Timeout | null = null
let healthCheckInterval: NodeJS.Timeout | null = null
let revalidateInterval: NodeJS.Timeout | null = null

function startHealthMonitoring() {
  // Clear any existing intervals
  if (pingInterval) clearInterval(pingInterval)
  if (healthCheckInterval) clearInterval(healthCheckInterval)
  if (revalidateInterval) clearInterval(revalidateInterval)

  // Send ping every 10 seconds
  pingInterval = setInterval(() => {
    if (socketInstance?.connected) {
      socketInstance.emit('ping')
    }
  }, 10000)

  // Check health every 5 seconds. socket.connected is the source of truth
  // for "is the underlying transport up?" — only when it IS up does the
  // heartbeat-staleness check matter (degraded means transport claims it's
  // open but pongs aren't coming back). Previously this timer ignored
  // socket.connected and could race with the disconnect handler.
  healthCheckInterval = setInterval(() => {
    const connected = !!socketInstance?.connected
    let newHealth: 'healthy' | 'degraded' | 'disconnected'

    if (!connected) {
      newHealth = 'disconnected'
    } else {
      const timeSinceLastHeartbeat = Date.now() - globalState.lastHeartbeat
      if (timeSinceLastHeartbeat > 60000) {
        newHealth = 'disconnected'
      } else if (timeSinceLastHeartbeat > 35000) {
        newHealth = 'degraded'
      } else {
        newHealth = 'healthy'
      }
    }

    if (newHealth !== globalState.connectionHealth) {
      console.warn(`Connection health changed: ${globalState.connectionHealth} -> ${newHealth}`)
      setGlobalState(prev => ({ ...prev, connectionHealth: newHealth }))
    }
  }, 5000)

  // Periodic revalidation — syncs with server truth, adds missed visitors,
  // removes stale ones. Runs unconditionally now: when the socket is up
  // it's a safety net for missed events; when the socket is down it's
  // the *only* source of new visitors. The previous gate bailed when
  // disconnected, which is exactly the case where staff staring at the
  // dashboard need to know a live visitor showed up.
  revalidateInterval = setInterval(async () => {
    try {
      const response = await api.get('/api/agent/active-chats')
      if (response.data.success && response.data.sessions) {
        const serverSessions = response.data.sessions
        const freshVisitorIds = new Set(serverSessions.map((s: any) => s.visitorId))
        const currentVisitors = globalState.visitors
        const currentVisitorIds = new Set(currentVisitors.map(v => v.visitorId))

        // Remove visitors that are no longer active on the server
        const staleRemoved = currentVisitors.filter(v => freshVisitorIds.has(v.visitorId))

        // Update handling states from server. Also reconcile the
        // messages array — if a visitor:message WS event was missed
        // (dashboard joined the org room after the message fired,
        // socket dropped, etc.), the server's snapshot has it and we
        // didn't. Merge by id, dedup, sort by timestamp. Without this,
        // a missed bot reply or visitor message stays missing forever
        // because the existing-visitor path never refreshed messages.
        const updated = staleRemoved.map(v => {
          const serverSession = serverSessions.find((s: any) => s.visitorId === v.visitorId)
          if (!serverSession) return v

          const serverMessages = (serverSession.messages || []) as Array<{
            id: string
            content: string
            senderType?: string
            timestamp: string
          }>

          // Reconcile messages. We need to match server messages to
          // local messages even when ids differ — that's the
          // "doubling" case. Two paths put placeholder ids on local
          // messages:
          //   - Agent's own optimistic append (sendMessageToVisitor
          //     uses Date.now()).
          //   - AI bot replies arriving via visitor:message WS event,
          //     which doesn't include the DB messageId.
          // For both, the server's authoritative version has a UUID
          // id that doesn't match. Without content+timestamp
          // matching, polling re-adds them as "new" messages → user
          // sees doubles.
          let mergedMessages = v.messages
          if (serverMessages.length > 0) {
            // Build id-keyed map of locals first (cheap dedup channel
            // for messages that DID arrive with a real DB id).
            const byId = new Map<string, Visitor['messages'][number]>()
            for (const m of v.messages) byId.set(m.id, m)

            // Helper: does any local message already represent this
            // server message under a different id? Match on content
            // (strict) AND senderType (strict, when both present)
            // AND timestamp within a 5s window. The window is wider
            // than the visitor:message dedup (2s) because the polling
            // path can land 15s later, and clock skew between the
            // optimistic Date.now() and the server-recorded timestamp
            // can drift up to ~3s under load.
            const isAlreadyLocal = (sm: typeof serverMessages[number]): boolean => {
              const smTime = new Date(sm.timestamp).getTime()
              for (const local of byId.values()) {
                if (local.content !== sm.content) continue
                if (sm.senderType && local.senderType !== sm.senderType) continue
                const localTime = new Date(local.timestamp).getTime()
                if (Math.abs(localTime - smTime) < 5000) return true
              }
              return false
            }

            let added = 0
            let replaced = 0
            for (const sm of serverMessages) {
              if (!sm.id) continue
              if (byId.has(sm.id)) continue
              if (isAlreadyLocal(sm)) {
                // Local has the same message under a placeholder id.
                // Replace the local entry with the server version so
                // future ticks dedup cleanly by id and the UI uses
                // the authoritative ordering/timestamp.
                for (const [localId, local] of byId.entries()) {
                  if (local.content !== sm.content) continue
                  if (sm.senderType && local.senderType !== sm.senderType) continue
                  if (Math.abs(new Date(local.timestamp).getTime() - new Date(sm.timestamp).getTime()) < 5000) {
                    byId.delete(localId)
                    byId.set(sm.id, {
                      id: sm.id,
                      content: sm.content,
                      senderType: (sm.senderType || 'visitor') as Visitor['messages'][number]['senderType'],
                      timestamp: sm.timestamp,
                    })
                    replaced++
                    break
                  }
                }
                continue
              }
              // Genuinely new — local was missing it.
              byId.set(sm.id, {
                id: sm.id,
                content: sm.content,
                senderType: (sm.senderType || 'visitor') as Visitor['messages'][number]['senderType'],
                timestamp: sm.timestamp,
              })
              added++
            }

            // Rebuild only if we actually changed anything.
            if (added > 0 || replaced > 0) {
              mergedMessages = Array.from(byId.values()).sort(
                (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
              )
            }
          }

          return {
            ...v,
            sessionId: serverSession.id,
            handlingState: serverSession.handlingState || (serverSession.agentId ? 'agent_handling' : 'ai_handling'),
            hasAgent: !!serverSession.agentId,
            agentId: serverSession.agentId || v.agentId,
            assignedAgentId: serverSession.assignedAgentId || v.assignedAgentId,
            messages: mergedMessages,
          }
        })

        // Add visitors from server that we don't have locally (missed WebSocket events)
        const newVisitors = serverSessions
          .filter((s: any) => !currentVisitorIds.has(s.visitorId))
          .map((session: any) => ({
            visitorId: session.visitorId,
            sessionId: session.id,
            userAgent: session.metadata?.userAgent,
            url: session.metadata?.url,
            timestamp: session.startedAt,
            hasAgent: !!session.agentId,
            agentId: session.agentId,
            handlingState: session.handlingState || (session.agentId ? 'agent_handling' : 'ai_handling'),
            assignedAgentId: session.assignedAgentId,
            lastActivityAt: session.lastActivityAt,
            contactInfo: session.metadata?.contactInfo || null,
            messages: session.messages || []
          }))

        const merged = [...updated, ...newVisitors]

        if (merged.length !== currentVisitors.length || merged.some((v, i) => v !== currentVisitors[i])) {
          setGlobalState(prev => ({ ...prev, visitors: merged }))
        }
      }
    } catch {
      // Non-critical — real-time events are the primary source
    }
  }, 15000)
}

// Subscribe to a specific socket event (for ad-hoc listeners outside the main hook)
export function onSocketEvent(event: string, handler: (...args: any[]) => void): () => void {
  if (socketInstance) {
    socketInstance.on(event, handler)
    return () => { socketInstance?.off(event, handler) }
  }
  // Socket not yet connected — poll until it is
  const checkInterval = setInterval(() => {
    if (socketInstance) {
      socketInstance.on(event, handler)
      clearInterval(checkInterval)
    }
  }, 500)
  return () => {
    clearInterval(checkInterval)
    socketInstance?.off(event, handler)
  }
}

function disconnectSocket() {
  if (socketInstance) {
    console.log('Disconnecting WebSocket...')
    socketInstance.disconnect()
    socketInstance = null
  }

  if (pingInterval) {
    clearInterval(pingInterval)
    pingInterval = null
  }

  if (healthCheckInterval) {
    clearInterval(healthCheckInterval)
    healthCheckInterval = null
  }

  if (revalidateInterval) {
    clearInterval(revalidateInterval)
    revalidateInterval = null
  }

  setGlobalState(() => ({
    isConnected: false,
    visitors: [],
    queueStats: {},
    lastHeartbeat: Date.now(),
    connectionHealth: 'disconnected'
  }))
}

function appendAgentMessageOptimistically(visitorId: string, message: string) {
  setGlobalState(prev => ({
    ...prev,
    visitors: prev.visitors.map(v =>
      v.visitorId === visitorId
        ? {
            ...v,
            handlingState: 'agent_handling' as const,
            messages: [...v.messages, {
              id: Date.now().toString(),
              content: message,
              senderType: 'agent' as const,
              timestamp: new Date().toISOString()
            }]
          }
        : v
    )
  }))
}

/**
 * Send an agent message to a visitor. WS path first; REST fallback if WS is
 * dropped — same shape as takeChatFromVisitor. The takeover only matters
 * once, but messages happen continuously, so silently failing here is the
 * worst possible UX (visitor sees "agent joined" but never any replies).
 */
async function sendMessageToVisitor(
  visitorId: string,
  message: string,
): Promise<{ ok: true } | { ok: false; reason: string }> {
  // WS path — instant when connected.
  if (socketInstance?.connected) {
    console.log('[AGENT_MSG] WS path — sending to visitor:', visitorId)
    socketInstance.emit('message', {
      type: 'agent:message',
      payload: {
        visitorId,
        message,
        timestamp: Date.now()
      }
    })
    appendAgentMessageOptimistically(visitorId, message)
    return { ok: true }
  }

  // Brief reconnect window — covers sub-second blips.
  console.warn('[AGENT_MSG] socket disconnected, waiting up to 1s for reconnect…')
  const start = Date.now()
  while (Date.now() - start < 1000) {
    await new Promise(r => setTimeout(r, 100))
    if (socketInstance?.connected) {
      socketInstance.emit('message', {
        type: 'agent:message',
        payload: { visitorId, message, timestamp: Date.now() }
      })
      appendAgentMessageOptimistically(visitorId, message)
      return { ok: true }
    }
  }

  // REST fallback. Server emits the visitor-side `agent:message` event from
  // the same in-memory io instance, so the visitor still sees the bubble in
  // real time even though our socket is down.
  console.warn('[AGENT_MSG] WS still down, falling back to REST')
  try {
    const apiMod = await import('@/lib/api')
    const api = apiMod.default
    const res = await api.post('/api/chat/agent-message', { visitorId, message })
    if (res.data?.success) {
      appendAgentMessageOptimistically(visitorId, message)
      return { ok: true }
    }
    return {
      ok: false,
      reason: res.data?.message || 'Could not send message.',
    }
  } catch (err: any) {
    const serverMessage = err?.response?.data?.message
    if (serverMessage) return { ok: false, reason: serverMessage }
    const status = err?.response?.status
    if (status) {
      return { ok: false, reason: `Server returned ${status}. Try again in a moment.` }
    }
    return {
      ok: false,
      reason: "Couldn't reach the server right now. Try again in a moment.",
    }
  }
}

/**
 * Revert the optimistic flip applied by takeChatFromVisitor. Called when
 * the server rejects a takeover (via takeover:error WS event OR a 4xx
 * REST response) so the visitor row goes back to whatever state it was
 * in when the agent clicked. No-op if there's no stash for visitorId.
 */
function revertOptimisticTakeover(visitorId: string): void {
  if (!pendingTakeovers.has(visitorId)) return
  const prior = pendingTakeovers.get(visitorId)!
  pendingTakeovers.delete(visitorId)
  setGlobalState(prev => ({
    ...prev,
    visitors: prev.visitors.map(v =>
      v.visitorId === visitorId ? { ...v, ...prior } : v
    )
  }))
}

/**
 * Take over a chat. Optimistically flips the visitor row to
 * agent-handling on click so the dashboard feels instant, then tries
 * the WS path (lower latency, server keeps the in-memory session warm)
 * and falls back to a REST endpoint if the socket is dropped for more
 * than a 2s reconnect grace window.
 *
 * On failure (REST 4xx, network error, or WS-path takeover:error), the
 * optimistic flip is reverted before/after the function resolves. The
 * caller (ChatPage) surfaces failures via toast, never alert().
 */
async function takeChatFromVisitor(
  visitorId: string,
): Promise<{ ok: true } | { ok: false; reason: string }> {
  console.log('[TAKEOVER] Taking over chat for visitor:', visitorId)

  // Optimistic flip — applied before BOTH the WS path AND the REST
  // fallback, so the visual feedback is instant regardless of which
  // transport succeeds. The WS takeover:error handler reverts on the
  // WS-path failure case; the REST catch block reverts on REST failure.
  setGlobalState(prev => ({
    ...prev,
    visitors: prev.visitors.map(v => {
      if (v.visitorId !== visitorId) return v
      if (!pendingTakeovers.has(visitorId)) {
        pendingTakeovers.set(visitorId, {
          hasAgent: v.hasAgent,
          agentId: v.agentId,
          handlingState: v.handlingState,
          assignedAgentId: v.assignedAgentId,
        })
      }
      return {
        ...v,
        hasAgent: true,
        agentId,
        handlingState: 'agent_handling' as const,
        assignedAgentId: agentId,
      }
    })
  }))

  // WS path — fast lane.
  if (socketInstance?.connected) {
    console.log('[TAKEOVER] WS path — emitting takeover for visitor:', visitorId)
    socketInstance.emit('takeover', { visitorId })
    return { ok: true }
  }

  // Brief reconnect window — socket.io reconnects every 1-30s, and a
  // sub-second drop is the common case. Don't fall back to REST for that.
  console.warn('[TAKEOVER] socket disconnected, waiting up to 2s for reconnect…')
  const start = Date.now()
  while (Date.now() - start < 2000) {
    await new Promise(r => setTimeout(r, 100))
    if (socketInstance?.connected) {
      console.log('[TAKEOVER] WS reconnected, emitting takeover')
      socketInstance.emit('takeover', { visitorId })
      return { ok: true }
    }
  }

  // REST fallback. Same auth headers as every other API call — the user
  // is logged in, just missing the socket.
  console.warn('[TAKEOVER] WS still down after grace, falling back to REST')
  try {
    const res = await api.post('/api/chat/takeover', { visitorId })
    if (res.data?.success) {
      console.log('[TAKEOVER] REST path succeeded')
      // The REST path doesn't trigger a takeover:success WS event for
      // this client (its socket is down), so drop the rollback snapshot
      // here — the optimistic state is now authoritative.
      pendingTakeovers.delete(visitorId)
      return { ok: true }
    }
    revertOptimisticTakeover(visitorId)
    return {
      ok: false,
      reason: res.data?.message || 'Could not take over this chat.',
    }
  } catch (err: any) {
    revertOptimisticTakeover(visitorId)
    const status = err?.response?.status
    const serverMessage = err?.response?.data?.message
    if (serverMessage) {
      return { ok: false, reason: serverMessage }
    }
    if (status) {
      return {
        ok: false,
        reason: `Server returned ${status}. Try again in a moment.`,
      }
    }
    return {
      ok: false,
      reason: "Couldn't reach the server right now. Try again in a moment.",
    }
  }
}

function endChatWithVisitor(visitorId: string) {
  if (socketInstance?.connected) {
    console.log('[END_CHAT] Ending chat for visitor:', visitorId)
    socketInstance.emit('agent:end-chat', { visitorId })
    // Optimistically remove from local state
    setGlobalState(prev => ({
      ...prev,
      visitors: prev.visitors.filter(v => v.visitorId !== visitorId)
    }))
  }
}

function sendTypingIndicator(visitorId: string, isTyping: boolean) {
  if (socketInstance?.connected) {
    socketInstance.emit('agent:typing', { visitorId, isTyping })
  }
}

// Hook that subscribes to singleton state
export function useConnectionHealth() {
  const [health, setHealth] = useState<WebSocketState['connectionHealth']>(globalState.connectionHealth)

  useEffect(() => {
    const handler = () => {
      setHealth(globalState.connectionHealth)
    }
    subscribers.add(handler)
    setHealth(globalState.connectionHealth)
    return () => { subscribers.delete(handler) }
  }, [])

  return health
}

export function useWebSocket() {
  const [state, setState] = useState<WebSocketState>(globalState)

  useEffect(() => {
    // Subscribe to state changes
    const handleStateChange = () => {
      setState({ ...globalState })
    }

    subscribers.add(handleStateChange)

    // Sync with current state immediately
    setState({ ...globalState })

    return () => {
      subscribers.delete(handleStateChange)
    }
  }, [])

  const connect = useCallback((newAgentId: string) => {
    connectSocket(newAgentId)
  }, [])

  const disconnect = useCallback(() => {
    disconnectSocket()
  }, [])

  const sendMessage = useCallback((visitorId: string, message: string) => {
    return sendMessageToVisitor(visitorId, message)
  }, [])

  const takeChat = useCallback((visitorId: string) => {
    return takeChatFromVisitor(visitorId)
  }, [])

  const endChat = useCallback((visitorId: string) => {
    endChatWithVisitor(visitorId)
  }, [])

  const sendTyping = useCallback((visitorId: string, isTyping: boolean) => {
    sendTypingIndicator(visitorId, isTyping)
  }, [])

  return {
    ...state,
    connect,
    disconnect,
    sendMessage,
    takeChat,
    endChat,
    sendTyping
  }
}
